'use client';

import { useCurrency } from '../CurrencyProvider';
import { NEARBY_CATEGORIES } from '../../_lib/nearby/nearby.config';
import type { NearbyCategoryKey, NearbyContext } from '../../_lib/nearby/nearby.types';
import { NearbySection } from './NearbySection';

export interface NearbyProps {
  /** Category (or categories) to skip from the rendered list — always
   *  includes the category the consuming page already represents (a
   *  page never cross-sells its own domain), and can carry extra
   *  categories a specific page wants hidden (e.g. Shows event pages
   *  hiding Sports + Restaurants while still showing Hotels/Shows). */
  excludeCategory: NearbyCategoryKey | NearbyCategoryKey[];
  latitude?: number;
  longitude?: number;
  cityName?: string;
  countryCode?: string;
  nationality?: string;
  /** YYYY-MM-DD — hotels check-in date. */
  checkin?: string;
  /** YYYY-MM-DD — sports/shows lower date bound. */
  dateFrom?: string;
  limit?: number;
  /** Sports event id — pass this on the sports event detail / booking-
   *  success pages so the Shows category uses the event-scoped
   *  same-city/same-date endpoint instead of a geo-radius search. */
  eventId?: number;
  /** Name of the restaurant/venue/hotel this page represents —
   *  interpolated into each strip's heading ("Hotels Near {name}"). */
  referenceName?: string;
  /** Optional display order for this page — e.g. `['sports', 'shows',
   *  'hotels']` on the restaurant pages. Categories not listed (or when
   *  this prop is omitted) fall back to `NEARBY_CATEGORIES`' declaration
   *  order, so most pages never need to set this. */
  categoryOrder?: NearbyCategoryKey[];
}

/**
 * Generic, config-drivenjj "Nearby" cross-sell — renders one strip per
 * category in `NEARBY_CATEGORIES` (see `nearby.config.ts`) except
 * `excludeCategory`. Adding a category anywhere in the app never touches
 * this file; it only needs a new adapter + one config entry.
 */
export function Nearby({ excludeCategory, categoryOrder, latitude, longitude, ...rest }: NearbyProps) {
  const { currencyCode } = useCurrency();

  if (latitude == null || longitude == null) return null;

  const ctx: NearbyContext = { ...rest, latitude, longitude, userCurrency: currencyCode };
  const allKeys = Object.keys(NEARBY_CATEGORIES) as NearbyCategoryKey[];
  const orderedKeys = categoryOrder
    ? [...categoryOrder, ...allKeys.filter((key) => !categoryOrder.includes(key))]
    : allKeys;
  const excluded = Array.isArray(excludeCategory) ? excludeCategory : [excludeCategory];
  const categories = orderedKeys.filter((key) => !excluded.includes(key));

  return (
    <div className="mb-4">
      {categories.map((key) => (
        <NearbySection key={key} categoryKey={key} ctx={ctx} />
      ))}
    </div>
  );
}
